import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/AddOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import LineItemsDialog from '@/components/common/LineItemsDialog';
import SearchableSelect from '@/components/common/SearchableSelect';
import StatusChip from '@/components/common/StatusChip';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import { PERMISSIONS } from '@/types/auth';
import type { WarehouseDto } from '@/types/organization';
import type { MaterialDto, PagedResult } from '@/types/materials';
import type {
  CreatePurchaseRequestRequest,
  PurchaseOrderDto,
  PurchaseRequestDto,
  PurchaseRequestLineDto,
  PurchaseRequestLineInput,
  UpdatePurchaseRequestRequest,
} from '@/types/purchasing';

export default function PurchaseRequestsPage() {
  const { t } = useTranslation('purchasing');
  const { formatDate } = useDateFormat();
  const queryClient = useQueryClient();
  const { data: config } = useProcessConfiguration();

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ['purchase-requests'],
    queryFn: async () => (await apiClient.get<PurchaseRequestDto[]>('/purchase-requests')).data,
  });
  const { data: warehouses = [] } = useQuery({
    queryKey: ['warehouses'],
    queryFn: async () => (await apiClient.get<WarehouseDto[]>('/warehouses')).data,
  });
  const { data: purchaseOrders = [] } = useQuery({
    queryKey: ['purchase-orders'],
    queryFn: async () => (await apiClient.get<PurchaseOrderDto[]>('/purchase-orders')).data,
    enabled: config?.purchaseOrderEnabled ?? false,
  });
  const { data: materialsPage } = useQuery({
    queryKey: ['materials', 'all-for-picker'],
    queryFn: async () => (await apiClient.get<PagedResult<MaterialDto>>('/materials', { params: { page: 1, pageSize: 200 } })).data,
  });
  const materials = materialsPage?.items ?? [];

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewing, setViewing] = useState<PurchaseRequestDto | null>(null);
  const [warehouseId, setWarehouseId] = useState('');
  const [lines, setLines] = useState<PurchaseRequestLineInput[]>([{ materialId: '', requestedQty: 0, remarks: null }]);

  const resetForm = () => {
    setEditingId(null);
    setWarehouseId('');
    setLines([{ materialId: '', requestedQty: 0, remarks: null }]);
  };

  // Only a still-Draft request can be edited — mirrors PurchaseRequestService.UpdateAsync's guard.
  const openEdit = (r: PurchaseRequestDto) => {
    setEditingId(r.id);
    setWarehouseId(r.warehouseId);
    setLines(r.lines.map((l) => ({ materialId: l.materialId, requestedQty: l.requestedQty, remarks: l.remarks })));
    setOpen(true);
  };

  const createMutation = useMutation({
    mutationFn: () => {
      const cleanLines = lines.filter((l) => l.materialId);
      if (editingId) {
        const payload: UpdatePurchaseRequestRequest = { warehouseId, lines: cleanLines };
        return apiClient.put(`/purchase-requests/${editingId}`, payload);
      }
      const payload: CreatePurchaseRequestRequest = { warehouseId, lines: cleanLines };
      return apiClient.post('/purchase-requests', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-requests'] });
      setOpen(false);
    },
  });

  const submitMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/purchase-requests/${id}/submit`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['purchase-requests'] }),
  });
  const approveMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/purchase-requests/${id}/approve`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['purchase-requests'] }),
  });
  const rejectMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/purchase-requests/${id}/reject`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['purchase-requests'] }),
  });
  const closeMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/purchase-requests/${id}/close`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['purchase-requests'] }),
  });

  // Same "stalled partial receipt only" rule as the Purchase Order/Gate Entry Close buttons — a PR
  // that hasn't been converted to a PO yet, or whose PO is still untouched (0% received) or
  // already fully/excess received, has nothing to manually retire.
  const hasPartiallyReceivedPo = (prId: string) => purchaseOrders.some((po) => po.purchaseRequestId === prId && po.status === 'PartiallyReceived');

  const columns: DataTableColumn<PurchaseRequestDto>[] = [
    { field: 'prNumber', headerName: t('purchaseRequest.table.number') },
    { field: 'warehouseName', headerName: t('purchaseRequest.table.warehouse'), filterable: true },
    { field: 'requestedByUserName', headerName: t('purchaseRequest.table.requestedBy'), filterable: true },
    { field: 'requestDate', headerName: t('purchaseRequest.table.date'), render: (r) => formatDate(r.requestDate) },
    { field: 'lines', headerName: t('purchaseRequest.table.lines'), render: (r) => r.lines.length },
    { field: 'status', headerName: t('purchaseRequest.table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'action', headerName: t('purchaseRequest.table.actions'), render: (r) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <Button size="small" onClick={() => setViewing(r)}>
            {t('view')}
          </Button>
          <IfPermitted permission={PERMISSIONS.PURCHASE_REQUEST_MANAGE}>
            <Stack direction="row" spacing={1} alignItems="center">
              {r.status === 'Draft' && (
                <>
                  <Button size="small" onClick={() => openEdit(r)}>
                    {t('edit')}
                  </Button>
                  <Button size="small" variant="outlined" disabled={submitMutation.isPending} onClick={() => submitMutation.mutate(r.id)}>
                    {t('purchaseRequest.submit')}
                  </Button>
                </>
              )}
              {r.status === 'Submitted' && (
                <>
                  <Button size="small" variant="outlined" disabled={approveMutation.isPending} onClick={() => approveMutation.mutate(r.id)}>
                    {t('purchaseRequest.approve')}
                  </Button>
                  <Button size="small" variant="outlined" color="error" disabled={rejectMutation.isPending} onClick={() => rejectMutation.mutate(r.id)}>
                    {t('purchaseRequest.reject')}
                  </Button>
                </>
              )}
              {r.status === 'Approved' && (
                r.isClosed ? (
                  <StatusChip status="Closed" />
                ) : hasPartiallyReceivedPo(r.id) ? (
                  <Tooltip title={t('purchaseRequest.closeHint')}>
                    <Checkbox size="small" disabled={closeMutation.isPending} onChange={() => closeMutation.mutate(r.id)} />
                  </Tooltip>
                ) : null
              )}
            </Stack>
          </IfPermitted>
        </Stack>
      ),
    },
  ];

  const lineColumns: DataTableColumn<PurchaseRequestLineDto>[] = [
    { field: 'materialCode', headerName: t('purchaseRequest.table.material'), render: (l) => `${l.materialCode} — ${l.materialName}` },
    { field: 'requestedQty', headerName: t('purchaseRequest.table.requestedQty') },
    { field: 'remarks', headerName: t('purchaseRequest.table.remarks'), render: (l) => l.remarks ?? '—' },
  ];

  return (
    <>
      <PageHeader
        title={t('purchaseRequest.title')}
        subtitle={t('purchaseRequest.subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.PURCHASE_REQUEST_MANAGE}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                resetForm();
                setOpen(true);
              }}
            >
              {t('purchaseRequest.add')}
            </Button>
          </IfPermitted>
        }
      />

      <DataTable columns={columns} rows={requests} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="purchase-requests" />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? t('edit') : t('purchaseRequest.add')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <SearchableSelect
              label={t('form.warehouse')} value={warehouseId} onChange={setWarehouseId} fullWidth
              options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
            />

            {lines.map((line, idx) => (
              <Stack key={idx} direction="row" spacing={1} alignItems="center">
                <SearchableSelect
                  label={t('form.material')} value={line.materialId} fullWidth size="small"
                  onChange={(materialId) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, materialId } : l)))}
                  options={materials.map((m) => ({ value: m.id, label: `${m.code} — ${m.name}` }))}
                />
                <TextField
                  type="number" label={t('form.quantity')} value={line.requestedQty} size="small" sx={{ minWidth: 120 }}
                  onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, requestedQty: Number(e.target.value) } : l)))}
                />
                <IconButton size="small" onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))} disabled={lines.length <= 1}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setLines((prev) => [...prev, { materialId: '', requestedQty: 0, remarks: null }])}
              sx={{ alignSelf: 'flex-start' }}
            >
              {t('form.addLine')}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>{t('cancel')}</Button>
          <Button
            variant="contained"
            disabled={!warehouseId || lines.some((l) => !l.materialId || l.requestedQty <= 0) || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      <LineItemsDialog
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        title={viewing?.prNumber ?? ''}
        subtitle={viewing ? `${viewing.warehouseName} — ${t('purchaseRequest.table.requestedBy')}: ${viewing.requestedByUserName}` : undefined}
        columns={lineColumns}
        rows={viewing?.lines ?? []}
      />
    </>
  );
}
